import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { postUnauth, refreshAccess, api } from "../lib/api";
import {
  clearSession,
  getSnapshot,
  hydrateFromStorage,
  setSession,
  setUser as setUserInStore,
  subscribe,
} from "../lib/auth-store";
import { legacyCapsForRole } from "../lib/legacy-role-capabilities";
import { queryClient } from "../lib/queryClient";
import type { AuthUser, CapabilityKey, LoginResponse } from "../lib/types";

// Refresh periodico de /auth/me (RBAC configuravel, plano 2026-09-23 secao 8
// W1): 5 min, igual ao smoke de aceite do plano ("em ate 5 min o menu dele
// muda"). Nao e polling agressivo — soma ao refresh no boot e ao voltar o
// foco da aba, que cobrem o caso comum (usuario troca de aba e volta).
const ME_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

interface AuthCtx {
  user: AuthUser | null;
  isAuthenticated: boolean;
  hasAccessToken: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  hasChannel: (slug: string) => boolean;
  /**
   * Fonte unica de autorizacao no web (RBAC configuravel). super_admin
   * sempre true (RN23, bypass por construcao — o unico slug com significado
   * especial no codigo). Demais papeis: `user.capabilities` quando a API
   * ja fala RBAC configuravel; se `capabilities` estiver AUSENTE (API
   * anterior a migracao 0024), cai no mapeamento fixo de
   * lib/legacy-role-capabilities.ts, que reproduz o gating de hoje.
   */
  can: (key: CapabilityKey) => boolean;
  mustChangePassword: boolean;
  booting: boolean;
}

const Ctx = createContext<AuthCtx | null>(null);

// Hydrate sincrono uma unica vez ANTES de qualquer render que dependa do user
let hydrated = false;
function ensureHydrated(): void {
  if (hydrated) return;
  hydrateFromStorage();
  hydrated = true;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  ensureHydrated();
  const navigate = useNavigate();

  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // I1 (C56 review): booting nasce true SOMENTE quando ha user hidratado sem access em
  // memoria (o caso do F5) — e o cenario em que o boot abaixo vai rodar um refresh async
  // antes de qualquer chamada autenticada ser segura. Se nao ha user hidratado, booting ja
  // nasce false (nao atrasa a tela de login). Lazy init le o snapshot uma unica vez.
  const [booting, setBooting] = useState<boolean>(() => !!(snapshot.user && !snapshot.access));
  const bootingRef = useRef(booting);
  bootingRef.current = booting;

  // Refresh proativo apos hydrate (C56 fix): se ha user espelhado mas SEM access em memoria
  // (caso classico do F5), renova a sessao via /auth/refresh ANTES de qualquer chamada
  // autenticada. O interceptor de retry-with-refresh ignora URLs /auth/*, entao esperar
  // /auth/me 401 nunca dispararia o refresh (sessao zumbi). refreshAccess() reusa o
  // coalescing in-flight do api.ts e ja limpa a sessao internamente se o refresh falhar.
  useEffect(() => {
    if (snapshot.user && !snapshot.access) {
      void (async () => {
        const newTok = await refreshAccess();
        if (!newTok) {
          // refresh_token expirado/revogado: sessao ja foi limpa por refreshAccess().
          // Manda pro login sem re-tentar (evita loop).
          navigate("/login", { replace: true });
          if (bootingRef.current) setBooting(false);
          return;
        }
        // token renovado: confirma/atualiza dados do usuario (papel,
        // capabilities, canais) — antes a resposta era descartada
        // (`.catch(() => {})` sem `.then`), que e a causa raiz medida no
        // plano secao 0: promover/rebaixar alguem no banco nao mudava o que
        // a sessao aberta via ate deslogar.
        await api
          .get<AuthUser>("/auth/me")
          .then((r) => setUserInStore(r.data))
          .catch(() => {
            /* falha aqui e nao-fatal: usuario ja tem access valido renovado */
          });
        if (bootingRef.current) setBooting(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh periodico de /auth/me (W1): no boot (quando ja ha access — o
  // ramo acima cobre o caso "sem access"), ao voltar o foco/visibilidade da
  // aba, e a cada 5 min enquanto a aba fica aberta. Sem isto a tela de
  // perfis nasceria com o MESMO defeito que este plano corrige: mudar o
  // perfil de alguem so valeria no proximo login.
  const { hasAccessToken: snapshotHasToken, isAuthenticated: snapshotIsAuth } = useMemo(
    () => ({ hasAccessToken: !!snapshot.access, isAuthenticated: !!snapshot.user }),
    [snapshot],
  );
  useEffect(() => {
    if (!snapshotIsAuth || !snapshotHasToken) return;

    const refreshMe = (): void => {
      void api
        .get<AuthUser>("/auth/me")
        .then((r) => setUserInStore(r.data))
        .catch(() => {
          /* nao-fatal: interceptor 401 ja trata sessao morta em qualquer chamada */
        });
    };

    // No boot com access ja em memoria (nao passou pelo ramo de refresh
    // acima) confirma o perfil uma vez.
    refreshMe();

    const onVisibility = (): void => {
      if (document.visibilityState === "visible") refreshMe();
    };
    window.addEventListener("focus", refreshMe);
    document.addEventListener("visibilitychange", onVisibility);
    const interval = window.setInterval(refreshMe, ME_REFRESH_INTERVAL_MS);

    return () => {
      window.removeEventListener("focus", refreshMe);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(interval);
    };
  }, [snapshotIsAuth, snapshotHasToken]);

  const login = useCallback(async (email: string, password: string): Promise<AuthUser> => {
    const r = await postUnauth<LoginResponse>("/auth/login", { email, password });
    setSession(r.access_token, r.refresh_token, r.user);
    return r.user;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* nao impede limpar local */
    } finally {
      clearSession();
      queryClient.clear();
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const can = useCallback(
    (key: CapabilityKey): boolean => {
      const user = snapshot.user;
      if (!user) return false;
      if (user.role === "super_admin") return true; // RN23: bypass total, por construcao
      if (user.capabilities !== undefined) return user.capabilities.includes(key);
      // API antiga (sem RBAC configuravel): fallback fixo por papel.
      return legacyCapsForRole(user.role).has(key);
    },
    [snapshot],
  );

  const value: AuthCtx = useMemo(() => {
    const user = snapshot.user;
    return {
      user,
      isAuthenticated: !!user,
      hasAccessToken: !!snapshot.access,
      login,
      logout,
      hasChannel: (slug: string) => {
        if (!user) return false;
        if (can("channels.all")) return true;
        return user.allowed_channels.includes(slug);
      },
      can,
      mustChangePassword: !!user?.must_change_pwd,
      booting,
    };
  }, [snapshot, login, logout, can, booting]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// AuthProvider e o hook useAuth coabitam de proposito neste modulo de contexto.
// O aviso de Fast Refresh aqui e apenas sobre HMR em dev; nao afeta runtime nem build.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de <AuthProvider>");
  return ctx;
}
